<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <span class="block"> {{ directT('dashboard.totalRevenue') }} </span>
          <span
            class="inline-block px-[8.3px] py-[1px] border text-sm ltr:ml-[10px] rtl:mr-[10px] rounded-[100px]"
            :class="percentageChange >= 0 ? 
              'text-success-700 border-success-300 bg-success-100' : 
              'text-danger-700 border-danger-300 bg-danger-100'"
          >
            {{ percentageChange >= 0 ? '+' : '' }}{{ percentageChange }}%
          </span>
        </div>
        <span class="block text-sm"> {{ directT('dashboard.last30Days') }} </span>
      </div>
      <h5 class="text-[18px] md:text-[20px] mb-0 mt-[4px]">{{ loading ? '...' : formatCurrency(totalRevenueAmount) }}</h5>
      <div
        class="mx-auto max-w-[200px] -mt-[20px] -mb-[10px] md:-mt-[23px] md:-mb-[16px]"
      >
        <apexchart
          v-if="isClient && !loading"
          type="bar"
          height="100"
          :options="totalRevenue"
          :series="revenue"
        ></apexchart>
        <div v-else-if="loading" class="flex justify-center items-center h-[100px]">
          <div class="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
      <ul>
        <li
          class="text-sm ltr:pl-[30px] rtl:pr-[30px] flex justify-between relative mb-[4px] last:mb-0"
        >
          <span
            class="inline-block bg-primary-500 ltr:left-0 rtl:right-0 absolute top-1/2 -translate-y-1/2 w-[20px] h-[5px] rounded-md"
          ></span>
          <span class="block"> {{ categoryNames[0] || directT('dashboard.fashion') }} </span>
          <span class="block"> {{ categoryPercentages[0] }}% </span>
        </li>
        <li
          class="text-sm ltr:pl-[30px] rtl:pr-[30px] flex justify-between relative mb-[4px] last:mb-0"
        >
          <span
            class="inline-block bg-primary-200 ltr:left-0 rtl:right-0 absolute top-1/2 -translate-y-1/2 w-[20px] h-[5px] rounded-md"
          ></span>
          <span class="block"> {{ categoryNames[1] || directT('dashboard.others') }} </span>
          <span class="block"> {{ categoryPercentages[1] }}% </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { supabase } from '@/lib/supabaseClient';

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Define types for order/revenue data
interface OrderData {
  id?: string;
  total?: number | null;
  totalamount?: number | null;
  created_at?: string;
  category?: string;
  vendor?: string;
  [key: string]: any;  // Allow any additional properties
}

export default defineComponent({
  name: "TotalRevenueChart",
  setup() {
    const { locale } = useI18n();
    const isClient = ref(false);
    const loading = ref(true);
    const totalRevenueAmount = ref(0);
    const percentageChange = ref(0);
    
    // Data for monthly revenue
    const monthlyRevenue = ref<number[]>([0, 0, 0, 0, 0, 0]);
    const otherRevenue = ref<number[]>([0, 0, 0, 0, 0, 0]);
    
    // Category data
    const categoryNames = ref<string[]>(['Category 1', 'Category 2']);
    const categoryPercentages = ref<number[]>([75, 25]);

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Format currency based on locale
    const formatCurrency = (amount: number): string => {
      if (locale.value === 'he' || locale.value === 'ar') {
        return `₪${amount.toLocaleString()}`;
      }
      return `$${amount.toLocaleString()}`;
    };

    // Computed property for revenue data
    const revenue = computed(() => [
      {
        name: categoryNames.value[0],
        data: monthlyRevenue.value,
      },
      {
        name: categoryNames.value[1],
        data: otherRevenue.value,
      },
    ]);
    
    // Chart options
    const totalRevenue = ref({
      chart: {
        type: "bar",
        height: 100,
        stacked: true,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: true,
        },
      },
      plotOptions: {
        bar: {
          columnWidth: "55%",
        },
      },
      colors: ["#605DFF", "#C2CDFF"],
      grid: {
        borderColor: "#ffffff",
      },
      stroke: {
        width: 2,
        show: true,
        colors: ["transparent"],
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: getLastSixMonths(),
        axisTicks: {
          show: false,
          color: "#B1BBC8",
        },
        axisBorder: {
          show: false,
          color: "#B1BBC8",
        },
        labels: {
          show: false,
          style: {
            colors: "#8695AA",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        show: false,
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
          },
        },
      },
      legend: {
        show: false,
        fontSize: "12px",
        position: "bottom",
        horizontalAlign: "center",
        itemMargin: {
          horizontal: 8,
          vertical: 0,
        },
        labels: {
          colors: "#64748B",
        },
        markers: {
          width: 9,
          height: 9,
          offsetX: -2,
          offsetY: -0.5,
        },
      },
      fill: {
        opacity: 1,
      },
    });

    // Get last six months for x-axis
    function getLastSixMonths(): string[] {
      const months = [];
      const date = new Date();
      
      for (let i = 0; i < 6; i++) {
        date.setMonth(date.getMonth() - 1);
        const monthName = date.toLocaleString('default', { month: 'short' });
        months.unshift(monthName);
      }
      
      return months;
    }

    // Create fallback sample data if needed
    const createSampleData = () => {
      // Sample data for revenue
      monthlyRevenue.value = [20, 40, 25, 60, 30, 50];
      otherRevenue.value = [20, 20, 25, 15, 35, 25];
      
      totalRevenueAmount.value = 165458;
      percentageChange.value = 10;
      
      categoryNames.value = [directT('dashboard.fashion'), directT('dashboard.others')];
      categoryPercentages.value = [75, 25];
    };

    // Function to fetch revenue data
    const fetchRevenueData = async () => {
      loading.value = true;
      console.log("Starting fetchRevenueData in TotalRevenueChart...");
      
      try {
        // Check Supabase connection
        if (!supabase) {
          console.error("Supabase client is not available");
          createSampleData();
          return;
        }
        
        // Calculate date range (last 30 days)
        const now = new Date();
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(now.getDate() - 30);
        
        const sixtyDaysAgo = new Date(now);
        sixtyDaysAgo.setDate(now.getDate() - 60);
        
        // Format dates for query
        const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();
        const sixtyDaysAgoStr = sixtyDaysAgo.toISOString();
        
        // Calculate six months ago for monthly data
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        const sixMonthsAgoStr = sixMonthsAgo.toISOString();
        
        // Query orders table
        console.log("Querying orders table for revenue data...");
        const { data: orders, error } = await supabase
          .from('orders')
          .select('*')
          .gte('created_at', sixMonthsAgoStr);
          
        if (error) {
          console.error("Error accessing orders table:", error);
          createSampleData();
          return;
        }
        
        if (!orders || orders.length === 0) {
          console.warn("No orders found in database");
          createSampleData();
          return;
        }
        
        console.log(`Found ${orders.length} orders for revenue calculation`);
        
        // Process the orders data
        const typedOrders: OrderData[] = orders.map(order => order as unknown as OrderData);
        
        // Calculate total revenue
        let totalRevenue = 0;
        typedOrders.forEach(order => {
          const orderAmount = parseFloat(String(order.total || order.totalamount || 0));
          totalRevenue += orderAmount;
        });
        
        totalRevenueAmount.value = totalRevenue;
        console.log("Total revenue:", totalRevenueAmount.value);
        
        // Filter orders for the last 30 days vs previous 30 days
        const recentOrders = typedOrders.filter(order => {
          if (!order.created_at) return false;
          const orderDate = new Date(order.created_at);
          return orderDate >= thirtyDaysAgo && orderDate <= now;
        });
        
        const previousOrders = typedOrders.filter(order => {
          if (!order.created_at) return false;
          const orderDate = new Date(order.created_at);
          return orderDate >= sixtyDaysAgo && orderDate < thirtyDaysAgo;
        });
        
        // Calculate recent and previous revenue
        let recentRevenue = 0;
        recentOrders.forEach(order => {
          recentRevenue += parseFloat(String(order.total || order.totalamount || 0));
        });
        
        let previousRevenue = 0;
        previousOrders.forEach(order => {
          previousRevenue += parseFloat(String(order.total || order.totalamount || 0));
        });
        
        // Calculate percentage change
        if (previousRevenue > 0) {
          const change = ((recentRevenue - previousRevenue) / previousRevenue) * 100;
          percentageChange.value = Math.round(change * 10) / 10; // Round to 1 decimal place
        } else if (recentRevenue > 0) {
          percentageChange.value = 100;
        } else {
          percentageChange.value = 0;
        }
        
        console.log("Recent revenue:", recentRevenue);
        console.log("Previous revenue:", previousRevenue);
        console.log("Percentage change:", percentageChange.value);
        
        // Group orders by category/vendor
        const categoryMap = new Map<string, number>();
        
        typedOrders.forEach(order => {
          // Determine category - use vendor if category not available
          const category = order.category || order.vendor || 'Other';
          const amount = parseFloat(String(order.total || order.totalamount || 0));
          
          if (categoryMap.has(category)) {
            categoryMap.set(category, categoryMap.get(category)! + amount);
          } else {
            categoryMap.set(category, amount);
          }
        });
        
        // Sort categories by revenue
        const sortedCategories = Array.from(categoryMap.entries())
          .sort((a, b) => b[1] - a[1]);
        
        // Take top category and combine the rest
        if (sortedCategories.length > 0) {
          const topCategory = sortedCategories[0];
          
          let otherCategoriesTotal = 0;
          for (let i = 1; i < sortedCategories.length; i++) {
            otherCategoriesTotal += sortedCategories[i][1];
          }
          
          const totalCategoryRevenue = topCategory[1] + otherCategoriesTotal;
          const topPercentage = Math.round((topCategory[1] / totalCategoryRevenue) * 100);
          const otherPercentage = 100 - topPercentage;
          
          categoryNames.value = [
            topCategory[0], 
            directT('dashboard.others')
          ];
          
          categoryPercentages.value = [topPercentage, otherPercentage];
          
          console.log("Category breakdown:", categoryNames.value, categoryPercentages.value);
        }
        
        // Calculate monthly revenue for the last 6 months
        const monthlyData = new Array(6).fill(0);
        const otherMonthlyData = new Array(6).fill(0);
        
        typedOrders.forEach(order => {
          if (!order.created_at) return;
          
          try {
            const orderDate = new Date(order.created_at);
            const orderAmount = parseFloat(String(order.total || order.totalamount || 0));
            
            // Check if this order falls within the last 6 months
            if (orderDate >= sixMonthsAgo) {
              // Calculate which month this order belongs to (0-5, where 5 is current month)
              const now = new Date();
              const monthDiff = (now.getFullYear() - orderDate.getFullYear()) * 12 + 
                               (now.getMonth() - orderDate.getMonth());
              
              if (monthDiff >= 0 && monthDiff < 6) {
                const monthIndex = 5 - monthDiff;
                
                // Determine if this order belongs to the top category
                const category = order.category || order.vendor || 'Other';
                
                if (category === categoryNames.value[0]) {
                  monthlyData[monthIndex] += orderAmount;
                } else {
                  otherMonthlyData[monthIndex] += orderAmount;
                }
              }
            }
          } catch (err) {
            console.error("Error processing order date:", err);
          }
        });
        
        // Normalize the data to reasonable chart values
        const maxValue = Math.max(...monthlyData, ...otherMonthlyData);
        if (maxValue > 0) {
          const normalized = monthlyData.map(val => Math.round((val / maxValue) * 100));
          const normalizedOther = otherMonthlyData.map(val => Math.round((val / maxValue) * 100));
          
          monthlyRevenue.value = normalized;
          otherRevenue.value = normalizedOther;
        } else {
          // Use sample data if no monthly data
          monthlyRevenue.value = [20, 40, 25, 60, 30, 50];
          otherRevenue.value = [20, 20, 25, 15, 35, 25];
        }
        
        console.log("Monthly revenue data:", monthlyRevenue.value);
        console.log("Other revenue data:", otherRevenue.value);
        
      } catch (err) {
        console.error('Error processing revenue data:', err);
        createSampleData();
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      isClient.value = true;
      console.log("TotalRevenueChart mounted, initiating data fetch...");
      fetchRevenueData();
    });

    return {
      isClient,
      loading,
      revenue,
      totalRevenue,
      directT,
      totalRevenueAmount,
      percentageChange,
      monthlyRevenue,
      otherRevenue,
      categoryNames,
      categoryPercentages,
      formatCurrency
    };
  },
});
</script>
